import { useEffect, useState } from 'react';

interface SOSResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: {
    remedy_name: string;
    disease_name: string;
    consultation_date: string;
  };
  onNewConsultation: () => void;
}

export default function SOSResultModal({
  isOpen,
  onClose,
  result,
  onNewConsultation
}: SOSResultModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200); // Duração da animação de saída
  };

  const handleNewConsultation = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onNewConsultation();
    }, 200);
  };

  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
        transition: 'opacity 300ms ease-in-out',
        opacity: isAnimating ? 1 : 0
      }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          transform: isAnimating ? 'scale(1)' : 'scale(0.95)',
          opacity: isAnimating ? 1 : 0,
          transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com Gradiente */}
        <div className="bg-gradient-to-r from-green-500 to-blue-500 p-6 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-heart-pulse-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Recomendação SOS</h2>
              <p className="text-sm text-white/90">Baseada nos seus sintomas</p>
            </div>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Card Principal - Resultado */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <i className="ri-medicine-bottle-line text-3xl text-purple-600"></i>
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Consulta processada com sucesso!
                </h3>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border-2 border-purple-300">
              <p className="text-sm text-gray-600 mb-2">Remédio recomendado:</p>
              <p className="text-2xl font-bold text-purple-700">
                {result.remedy_name}
              </p>
            </div>
          </div>

          {/* Card Instruções de Uso */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-clipboard-line text-xl text-blue-600"></i>
              <h3 className="text-base font-bold text-gray-800">Como Usar</h3>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Em caso de crise tome a medicação a cada 15 minutos durante 1 hora
            </p>
          </div>

          {/* Seção Importante */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-5">
            <div className="flex items-start gap-2 mb-2">
              <i className="ri-alert-line text-xl text-yellow-700"></i>
              <h3 className="text-base font-bold text-yellow-900">IMPORTANTE</h3>
            </div>
            <p className="text-sm text-yellow-900 leading-relaxed">
              Esta é uma recomendação inicial baseada nos sintomas relatados. 
              Consulte sempre seu homeopata para avaliação mais completa
            </p>
          </div>

          {/* Seção Prazo de Melhora */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-time-line text-xl text-gray-600"></i>
              <h3 className="text-base font-bold text-gray-800">Prazo de Melhora Esperado</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                <span>Sintomas agudos: 24h</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                <span>Se não melhorar: consulte seu homeopata</span>
              </li>
            </ul>
          </div>

          {/* Seção Dicas */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-lightbulb-line text-xl text-blue-600"></i>
              <h3 className="text-base font-bold text-gray-800">Dicas</h3>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Guarde seus medicamentos longe do calor, umidade e odores fortes.
            </p>
          </div>

        </div>

        {/* Footer com Botões e Data */}
        <div className="border-t border-gray-100 p-6 bg-white rounded-b-3xl">
          <div className="flex gap-3 mb-4">
            <button
              onClick={handleNewConsultation}
              className="flex-1 py-3 px-6 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              Nova Consulta
            </button>
            <button
              onClick={handleClose}
              className="flex-1 py-3 px-6 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
            >
              Fechar
            </button>
          </div>
          
          <p className="text-xs text-center text-gray-500">
            Consulta realizada em {formatDate(result.consultation_date)}
          </p>
        </div>
      </div>
    </div>
  );
}
